// OWNER: Agent C. Loan cards: outstanding, EMI, next due, % paid.
import { useState } from "react";
import { useNavigate } from "react-router";
import { Text } from "@cloudflare/kumo/components/text";
import { Empty } from "@cloudflare/kumo/components/empty";
import { Meter } from "@cloudflare/kumo/components/meter";
import { LayerCard } from "@cloudflare/kumo/components/layer-card";
import { Button } from "@cloudflare/kumo/components/button";
import { BankIcon, PlusIcon } from "@phosphor-icons/react";
import { formatMoney, formatPercent } from "@shared/format";
import { useLoans, useIsAdmin } from "../../lib/hooks";
import { PageHeader } from "../../components/PageHeader";
import { CardSkeleton } from "../../components/Skeletons";
import { LoanFormDialog } from "./LoanFormDialog";

export default function LoansPage() {
  const loans = useLoans();
  const navigate = useNavigate();
  const isAdmin = useIsAdmin();
  const [addOpen, setAddOpen] = useState(false);

  return (
    <div className="space-y-6 pb-8">
      <PageHeader
        title="Loans"
        action={
          <Button
            variant="primary"
            icon={<PlusIcon />}
            disabled={!isAdmin}
            title={isAdmin ? undefined : "Admins only"}
            onClick={() => setAddOpen(true)}
          >
            Add loan
          </Button>
        }
      />

      <LoanFormDialog
        state={addOpen ? { mode: "create" } : null}
        onClose={() => setAddOpen(false)}
        onSaved={loans.reload}
      />

      {loans.status === "loading" && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <CardSkeleton />
          <CardSkeleton />
        </div>
      )}
      {loans.status === "error" && <Text variant="error">{loans.error.message}</Text>}
      {loans.status === "success" && loans.data.length === 0 && (
        <Empty
          icon={<BankIcon size={48} className="text-kumo-inactive" />}
          title="No loans on record"
          description="Loans you add will show outstanding balance, EMI, and payoff progress here."
          contents={
            <Button
              variant="primary"
              disabled={!isAdmin}
              title={isAdmin ? undefined : "Admins only"}
              onClick={() => setAddOpen(true)}
            >
              Add a loan
            </Button>
          }
        />
      )}
      {loans.status === "success" && loans.data.length > 0 && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {loans.data.map((loan) => {
            const paidPct = loan.principal > 0 ? ((loan.principal - loan.outstandingPrincipal) / loan.principal) * 100 : 0;
            return (
              <LayerCard
                key={loan.id}
                className="cursor-pointer"
                role="button"
                tabIndex={0}
                onClick={() => navigate(`/loans/${loan.id}`)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") navigate(`/loans/${loan.id}`);
                }}
              >
                <LayerCard.Secondary className="flex items-center justify-between">
                  <span>{loan.name}</span>
                  <span className="text-kumo-subtle">{loan.lender}</span>
                </LayerCard.Secondary>
                <LayerCard.Primary className="space-y-3">
                  <div>
                    <Text variant="secondary" size="xs">
                      Outstanding
                    </Text>
                    <div className="text-xl font-semibold text-kumo-strong">{formatMoney(loan.outstandingPrincipal, loan.currency)}</div>
                  </div>
                  <Meter label="Paid off" value={paidPct} customValue={formatPercent(paidPct, 1).replace("+", "")} />
                  <div className="flex justify-between text-sm text-kumo-subtle">
                    <span>EMI {loan.emiAmount ? formatMoney(loan.emiAmount, loan.currency) : "—"}</span>
                    <span>Next due {loan.nextDueDate ?? "—"}</span>
                  </div>
                </LayerCard.Primary>
              </LayerCard>
            );
          })}
        </div>
      )}
    </div>
  );
}
